<template>
  <div class="em-collapsible">
    <div class="em-collapsible__header" @click="toggleOpen">
      <h3 class="em-collapsible__title">{{ title }}</h3>
      <Button
        type="primary"
        size="small"
        @click.stop="toggleOpen"
        shape="circle"
        :icon="isOpen ? 'remove-outline' : 'add-outline'"
      >
      </Button>
    </div>
    <Transition name="collapse" @enter="onEnter" @leave="onLeave">
      <div v-if="isOpen" class="em-collapsible__content">
        <slot />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Button } from '@/common/components/ui/';

interface Props {
  title: string;
  open?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
});

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

const localOpen = ref(props.open);

const isOpen = computed({
  get: () => localOpen.value,
  set: (value) => {
    localOpen.value = value;
    emit('update:open', value);
  },
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const onEnter = (el: Element) => {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = '0';
  void htmlEl.offsetHeight; // force reflow
  htmlEl.style.height = htmlEl.scrollHeight + 'px';
};

const onLeave = (el: Element) => {
  const htmlEl = el as HTMLElement;
  htmlEl.style.height = htmlEl.scrollHeight + 'px';
  void htmlEl.offsetHeight; // force reflow
  htmlEl.style.height = '0';
};
</script>

<style scoped>
.em-collapsible {
  border: 1px solid var(--ion-input-border-color);
  border-radius: var(--ion-radius-3xl);
  overflow: visible;
}

.em-collapsible__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--ion-space-4);
  border-radius: var(--ion-radius-3xl);
  background-color: var(--ion-input-background);
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;
}

.em-collapsible__header:hover {
  background-color: var(--ion-input-border-color, var(--ion-input-background));
}

.em-collapsible__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ion-text-color);
}

.em-collapsible__content {
  padding: var(--ion-space-4);
  background-color: var(--ion-background-color);
  border-radius: var(--ion-radius-3xl);
}

.collapse-enter-active,
.collapse-leave-active {
  transition: height 0.3s ease;
  overflow: visible;
}

.collapse-enter-from,
.collapse-leave-to {
  height: 0;
  overflow: hidden;
}
</style>
